import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import type { DialogProps } from '@radix-ui/react-dialog'
import * as Dialog from '@radix-ui/react-dialog'

import { Carousel } from '@jotta/carousel'
import { useFeatures } from '@jotta/grpc-connect-client/config'
import { getWopiActionTypeKey, isWopi } from '@jotta/grpc-js-client/wopiService'
import { ItemAction } from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'
import { useStableRef } from '@jotta/hooks'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { popPath } from '@jotta/utils/pathUtils'

import { DisplayActiveFileActionDialog } from '../FileActions/DisplayActiveFileActionDialog'
import { apiPathToRoute } from '../FileRoute/utils/apiPathToRoute'
import { useFileRouteParams } from '../FileRoute/utils/getFileRouteParams'
import { WopiViewContainer } from '../FileView/WopiView/WopiViewContainer'
import type { PathItemStore } from '../PathItem/PathItemStore'

import { createFileCarouselHeader } from './FileCarouselHeader'
import { FileCarouselView } from './FileCarouselView'
import { getFileCarouselItems } from './getFileCarouselItems'
import type { ImageFileEditorOverlayHandle } from './ImageFileEditorOverlay'

import styles from './FileCarousel.module.scss'

const debug = Debug('jotta:files:FileCarousel')

const loadImageFileEditorOverlay = () => import('./ImageFileEditorOverlay')
const LazyImageFileEditorOverlay = lazy(() =>
  loadImageFileEditorOverlay().then(module => ({
    default: module.ImageFileEditorOverlay,
  })),
)

function useCarouselNav(hasFile: boolean) {
  // const [canNavBack, setCanNavBack] = useState(!hasFile)
  const canNavBack = useRef(!hasFile)
  const navigate = useNavigate()
  const location = useLocation()
  const locationRef = useStableRef(location)

  if (!hasFile) {
    canNavBack.current = true
  }

  const nav = useCallback(() => {
    if (canNavBack.current) {
      navigate(-1)
    } else {
      navigate(popPath(locationRef.current.pathname), {
        replace: true,
      })
    }
  }, [navigate, locationRef])

  return nav
}

export interface FileCarouselProps extends DialogProps {
  open?: boolean
  loading?: boolean
  file?: PathItemStore
  files?: PathItemStore[]
}
export const FileCarousel = observer<FileCarouselProps>(function FileCarousel({
  open = false,
  file,
  files,
  children,
  loading = false,
  ...props
}) {
  const [data, setData] = useState(() => getFileCarouselItems(file, files))
  const [search] = useSearchParams()
  const features = useFeatures()
  const { context, sortBy, viewMode, query } = useFileRouteParams()
  const wopiAction = getWopiActionTypeKey(search.get('action'))
  const showWopiEditView =
    file &&
    isWopi(file.name, features.wopiTestMode) &&
    search.get('action') === 'edit' &&
    wopiAction !== 'EMBEDVIEW'
  const navigate = useNavigate()
  const [editorOpen, setEditorOpen] = useState(false)
  // Imperative handle on the editor overlay so Esc/outside-click routes
  // through the editor's discard-confirm flow rather than dropping
  // unsaved edits on the floor.
  const editorRef = useRef<ImageFileEditorOverlayHandle>(null)

  const setActiveIndex = useCallback(
    (i: number) => {
      if (i >= 0 && i < data.items.length) {
        const item = data.items[i]
        const route = apiPathToRoute(item.data.path, {
          context,
          sortBy,
          viewMode,
          query,
        })
        navigate(route.encodedPath, {
          replace: true,
        })
      }
    },
    [context, sortBy, viewMode, query, data.items, navigate],
  )

  useEffect(() => {
    if (!loading) {
      setData(getFileCarouselItems(file, files))
    }
  }, [file, files, files?.length, loading])

  const navBack = useCarouselNav(Boolean(file))
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        return
      }
      if (editorOpen) {
        // Editor active: route the close through its discard-confirm so
        // unsaved edits aren't silently dropped. The editor calls back to
        // `onClose` (setEditorOpen(false)) once the user confirms.
        editorRef.current?.requestCancel()
        return
      }
      navBack()
    },
    [editorOpen, navBack],
  )

  const handleOpenEditor = useCallback(() => {
    void loadImageFileEditorOverlay()
    setEditorOpen(true)
  }, [])

  const fileCarouselHeader = useMemo(
    () =>
      createFileCarouselHeader({
        onEditImage: handleOpenEditor,
      }),
    [handleOpenEditor],
  )

  const prevIndex = useRef(data.index)

  // data.index needs to be -1 and file undefined before we can safely close carousel (they do not update simultaneously)
  useEffect(() => {
    if (
      open &&
      !file &&
      data.index < 0 &&
      prevIndex.current >= 0 &&
      data.items.length > 0
    ) {
      // File has been moved or deleted
      setActiveIndex(prevIndex.current % data.items.length)
    } else if (!loading && open && !file && data.index < 0) {
      handleOpenChange(false)
    }

    prevIndex.current = data.index
  }, [
    data.index,
    open,
    handleOpenChange,
    loading,
    file,
    data.items.length,
    setActiveIndex,
  ])

  // Skip carousel completely when editing with WOPI - Had issues with conflicting
  // event handlers when both were active
  if (showWopiEditView) {
    return (
      <>
        <WopiViewContainer
          item={file.data}
          onAction={(wopiAction, data) => {
            switch (wopiAction) {
              case 'File_Rename': {
                const newFileName = data
                if (!newFileName || !file?.path) {
                  debug(`wopi: invalid event=${wopiAction} data=${data}`)
                  return
                }

                // changes file name and path immediately
                file.renameTo(newFileName)
                const newPath = file.path

                // Generate the new route URL
                const newRoute = apiPathToRoute(newPath, {
                  context,
                  sortBy,
                  viewMode,
                  query,
                })

                // Update browser URL silently, preserving query params and hash
                const currentUrl = new URL(window.location.href)
                const newUrl = new URL(
                  newRoute.encodedPath,
                  window.location.origin,
                )
                newUrl.search = currentUrl.search // preserve ?action=edit etc.
                newUrl.hash = currentUrl.hash // preserve any hash fragment

                // Root Cause:
                //   - route is computed from this.router.pathname (line 96-98)
                //   - folder.currentFile does this.entries.get(this.root.route.apiPath)
                //   - When renameTo() calls removeItem(oldPath), the entry at the old path is deleted
                //   - BUT router.pathname still contains the old path (replaceState doesn't update React Router)
                //   - So currentFile becomes undefined, triggering re-renders/navigation
                //
                //window.history.replaceState(null, '', newUrl.toString())

                // react-router alternative:
                // does not seem to work, it tries to actually navigate even with replace: true, and it does not retain query params
                // https://reactrouter.com/api/hooks/useNavigate
                navigate(
                  {
                    pathname: newRoute.encodedPath,
                    search: currentUrl.search,
                    hash: currentUrl.hash,
                  },
                  {
                    preventScrollReset: true,
                    replace: true,
                  },
                )
                break
              }
              case 'UI_Close': {
                const wantedPath = file?.path
                if (!wantedPath) {
                  debug('file is missing path: ', file)
                  return
                }
                file?.folder.actions.dispatch(
                  // GoToFolder is an action that takes you to the parent of the argument...
                  'GoToFolder',
                  'FOLDER_LIST',
                  wantedPath,
                )
                break
              }
              case 'UI_Sharing': {
                file.folder.actions.dispatch('Share', 'FOLDER_LIST', file?.path)
                break
              }
              case 'UI_Edit': {
                const wantedPath = file?.path
                if (!wantedPath) {
                  debug('file is missing path: ', file)
                  return
                }
                if (!file?.data.actionList?.includes(ItemAction.UPDATE)) {
                  debug('file is not editable: ', file)
                  return
                }
                file?.folder.actions.dispatch(
                  'OpenDocument',
                  'FOLDER_LIST',
                  wantedPath,
                )
                break
              }
            }
          }}
        />
        {/* note: we need the DisplayActiveFileActionDialog present here to handle actions like UI_Sharing from the WOPI frame */}
        <DisplayActiveFileActionDialog isNestedModal store={file.folder} />
      </>
    )
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange} {...props}>
      {children}
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay}>
          <Dialog.Content
            className={styles.container}
            data-testid="FileCarousel"
          >
            {!!data.items.length && data.index >= 0 && (
              <Carousel
                ItemHeader={fileCarouselHeader}
                ItemView={FileCarouselView}
                items={data.items}
                currentItemIndex={data.index}
                getId={getPathItemId}
                setActiveIndex={setActiveIndex}
              />
            )}
            {file && editorOpen && (
              <Suspense
                fallback={
                  <LoadingOverlay
                    id="ImageFileEditorOverlay"
                    open
                    position="absolute"
                    darkMode
                  />
                }
              >
                <LazyImageFileEditorOverlay
                  ref={editorRef}
                  item={file}
                  onClose={() => setEditorOpen(false)}
                />
              </Suspense>
            )}
          </Dialog.Content>
          {file && open && (
            <DisplayActiveFileActionDialog isNestedModal store={file.folder} />
          )}
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
})

function getPathItemId(item: PathItemStore) {
  return item.data.path
}
